// ============================================================
// agent_chat_controller.js
// 호스티드 에이전트 채팅 — 런타임 SSE(제어 채널) 소비 컨트롤러
// ============================================================
// 동작 방식:
//   - connect() 시 eventsUrl(동일 출처 프록시)로 EventSource 를 연다(run_id 있을 때만).
//   - SSE 이벤트별 렌더:
//       message(role, content)                         → 말풍선
//       tool_call(name, args, result, status, scope_used) → tool-call 카드
//       status(running|done|stopped)                   → 상태 라벨 갱신 + done/stopped면 스트림 종료
//       confirm_required(toolCallId, tool, args)       → 고위험 승인 프롬프트(승인/거부 폼).
//                                                        승인/거부는 fetch 로 인플레이스 전송한다
//                                                        (페이지 이동 없음 — 스크롤 위치 유지).
//       display(toolUseId, document, heightPx, provider{label,labelKey}, template, sha256, actions?)
//                                                      → ADP 카드(sandbox iframe + 출처 칩 + 인텐트 칩)
//                                                        template/sha256 은 감사·디버그용(렌더 미사용).
//                                                        actions = 선언형 인텐트(§8.5) — 컴포저를 채울 뿐 전송하지 않는다.
//   - 라벨은 전부 ERB(i18n)에서 data-*-value 로 주입받는다(JS 내 하드코딩 없음).
//   - XSS 방지: 모델/툴 출력은 textContent 로만 넣는다(innerHTML 금지).
//
// 사용 위치: agent_chats/show.html.erb 의 라이브 영역
// 값: eventsUrl(SSE), confirmUrl(승인 POST), conversationId(폼 hidden), labels(i18n 묶음)
// ============================================================

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  // stream: 라이브 말풍선 컨테이너(display:contents), scroller: 실제 스크롤 박스(이력+라이브 공유),
  // input: 컴포저 textarea, pulse: 실행 중 펄스 점.
  static targets = ["stream", "status", "scroller", "input", "pulse"]
  static values  = {
    eventsUrl: String,
    confirmUrl: String,
    conversationId: String,
    // commandsUrl: 인플레이스 전송 시 fetch 로 POST 할 명령 엔드포인트(페이지 이동 없음).
    commandsUrl: String,
    // returnView: "talk" 이면 confirm 폼에 hidden return_view 를 실어 전송 후 /talk 에 머물게 한다.
    // 값이 없으면(기존 /agents/:id/chat) 필드를 덧붙이지 않는다(회귀 없음).
    returnView: String,
    // scanUrl: 블록체인 스캔 사이트 기본 URL. ERB(ApplicationHelper#blockchain_scan_base)가
    //          주입하는 단일 출처다 — JS 에 도메인을 하드코딩하지 않는다(운영 중 교체 대비).
    //          값이 비면 링크화를 건너뛰고 평문으로만 렌더한다(안전측 폴백).
    scanUrl: String,
    labels: Object
  }

  connect() {
    // 서버사이드 이력이 이미 렌더돼 있으므로, 진입 시 최신(하단)으로 스크롤한다.
    this._scrollToBottom()
    // run_id 가 없으면(eventsUrl 비어있음) 라이브 구독은 하지 않는다(이력만 표시).
    if (!this.eventsUrlValue) return
    this._open()
  }

  // ── 컴포저: Enter=전송 / Shift+Enter=줄바꿈 ────────────────
  // WHY: 진짜 채팅 UX. Enter 로 즉시 전송하되 Shift+Enter 는 줄바꿈을 허용한다.
  keydown(e) {
    if (e.key !== "Enter" || e.shiftKey || e.isComposing) return
    e.preventDefault()
    const form = e.target.closest("form")
    if (form) form.requestSubmit()
  }

  // ── 명령 전송 (인플레이스 · 페이지 이동 없음) ──────────────────
  // 폼 submit 을 가로채 fetch 로 보낸다. 서버 { runId, eventsUrl, confirmUrl } 응답이면
  // 사용자 말풍선을 즉시 그리고 그 run 의 SSE 를 그 자리에서 연다(새로고침 방지).
  async send(e) {
    if (e) e.preventDefault()
    const input = this.hasInputTarget ? this.inputTarget : null
    const text = input ? input.value.trim() : ""
    if (!text || !this.commandsUrlValue) return

    const token = document.querySelector('meta[name="csrf-token"]')
    const body = new URLSearchParams()
    body.set("conversation_id", this.conversationIdValue || "")
    body.set("text", text)
    if (this.hasReturnViewValue && this.returnViewValue) body.set("return_view", this.returnViewValue)

    this._appendBubble("user", text)
    if (input) { input.value = ""; input.style.height = "" }
    const runningLabel = (this.labelsValue.statuses && this.labelsValue.statuses.running) || ""
    this._setStatus(runningLabel)

    let res
    try {
      res = await fetch(this.commandsUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": token ? token.content : ""
        },
        body: body.toString()
      })
    } catch (_) {
      this._setStatus(this.labelsValue.disconnected || "")
      return
    }
    if (!res.ok) {
      let msg = this.labelsValue.command_failed || ""
      try { const j = await res.json(); if (j && j.error) msg = j.error } catch (_) {}
      this._setStatus(msg)
      return
    }
    const data = await res.json().catch(() => ({}))
    if (!data.runId || !data.eventsUrl) { this._setStatus(this.labelsValue.command_failed || ""); return }
    if (this.hasPulseTarget) this.pulseTarget.style.display = ""
    this.confirmUrlValue = data.confirmUrl || ""
    this._openUrl(data.eventsUrl)
    this._showTyping()
  }

  // 로컬 말풍선 추가(사용자 즉시 표시용). _onMessage 와 동일 DOM 구조.
  _appendBubble(role, text) {
    const wrap = document.createElement("div")
    wrap.className = "ac-msg ac-msg--" + role
    const r = document.createElement("span")
    r.className = "ac-msg-role"
    r.textContent = (this.labelsValue.roles && this.labelsValue.roles[role]) || role
    const b = document.createElement("div")
    b.className = "ac-msg-body"
    this._linkifyInto(b, text)
    wrap.appendChild(r)
    wrap.appendChild(b)
    this._append(wrap)
  }

  disconnect() {
    this._hideTyping()
    if (this._source) this._source.close()
  }

  // ── SSE 연결 ───────────────────────────────────────────────
  _open() { this._openUrl(this.eventsUrlValue) }

  _openUrl(url) {
    if (!url) return
    if (this._source) this._source.close()
    const es = new EventSource(url)
    this._source = es
    es.addEventListener("message", (e) => this._onMessage(e))
    es.addEventListener("tool_call", (e) => this._onToolCall(e))
    es.addEventListener("display", (e) => this._onDisplay(e))
    es.addEventListener("status", (e) => this._onStatus(e))
    es.addEventListener("confirm_required", (e) => this._onConfirm(e))
    es.onerror = () => this._setStatus(this.labelsValue.disconnected || "")
  }

  // ── 타이핑 인디케이터 (레이턴시 동안 "···" 반복) ──────────────
  // 에이전트가 응답을 생성하는 동안(명령 전송~첫 메시지, 툴 결과~다음 스텝) 점 애니메이션을
  // 라이브 영역 하단에 표시한다. 메시지·툴카드·컨펌·종료 시 제거한다(idempotent).
  _showTyping() {
    if (this._typingEl && this._typingEl.isConnected) return
    const wrap = document.createElement("div")
    wrap.className = "ac-msg ac-msg--agent ac-typing"
    const role = document.createElement("span")
    role.className = "ac-msg-role"
    role.textContent = (this.labelsValue.roles && this.labelsValue.roles.agent) || "agent"
    const dots = document.createElement("div")
    dots.className = "ac-typing-dots"
    dots.setAttribute("aria-label", "…")
    for (let i = 0; i < 3; i++) dots.appendChild(document.createElement("span"))
    wrap.appendChild(role)
    wrap.appendChild(dots)
    this._typingEl = wrap
    this._append(wrap)
  }

  _hideTyping() {
    if (this._typingEl) { this._typingEl.remove(); this._typingEl = null }
  }

  _parse(e) {
    try { return JSON.parse(e.data) } catch (_) { return {} }
  }

  // ── 메시지 말풍선 ──────────────────────────────────────────
  _onMessage(e) {
    const d = this._parse(e)
    this._hideTyping()
    const wrap = document.createElement("div")
    wrap.className = "ac-msg ac-msg--" + (d.role || "agent")

    const role = document.createElement("span")
    role.className = "ac-msg-role"
    role.textContent = (this.labelsValue.roles && this.labelsValue.roles[d.role]) || d.role || ""

    const body = document.createElement("div")
    body.className = "ac-msg-body"
    // 런타임 SSE message 이벤트는 { role, text } 형태다(content 아님). text 우선, content 는 하위호환.
    this._linkifyInto(body, d.text || d.content || "")

    wrap.appendChild(role)
    wrap.appendChild(body)
    this._append(wrap)
  }

  // ── tool-call 카드 (접기/펼치기, 툴명·인자·결과·사용 scope) ──
  // 이력 렌더(_bubble.html.erb)와 동일한 <details class="ac-tool"> 구조/클래스를 생성한다.
  _onToolCall(e) {
    const d = this._parse(e)
    this._hideTyping()
    const card = document.createElement("details")
    card.className = "ac-tool"

    const head = document.createElement("summary")
    head.className = "ac-tool-head"
    const name = document.createElement("span")
    name.className = "ac-tool-name"
    name.textContent = "⚙ " + (d.name || "")
    head.appendChild(name)
    if (d.status) {
      const st = document.createElement("span")
      st.className = "ac-tool-status ac-tool-status--" + d.status
      st.textContent = d.status
      head.appendChild(st)
    }
    card.appendChild(head)

    const body = document.createElement("div")
    body.className = "ac-tool-body"
    body.appendChild(this._row(this.labelsValue.args, this._stringify(d.args)))
    if (d.result !== undefined) body.appendChild(this._row(this.labelsValue.result, this._stringify(d.result)))
    // scope_used 는 라이브 감사 정보(이력엔 없음). 있을 때만 노출.
    if (d.scope_used) body.appendChild(this._row(this.labelsValue.scope, this._stringify(d.scope_used)))
    card.appendChild(body)

    this._append(card)
    // 툴 결과 후 다음 스텝(LLM 재호출) 대기 — 컨펌 대기가 아니면 타이핑 재표시.
    if (d.status !== "pending_confirm") this._showTyping()
  }

  // ── ADP(Agent Display Protocol) 카드 ────────────────────────
  // 런타임이 CSP meta 까지 넣어 "완성한 HTML 문서 문자열"(d.document)을 격리 iframe 에 태운다.
  // webxcom 은 문서를 만들지도 고치지도 않는다 — 이력 렌더(_adp_card.html.erb)와 동일한
  // .ac-adp 클래스 계약/속성 계약을 생성한다.
  //
  // ⚠ 보안 (변형 금지 — 어기면 즉시 XSS)
  //   1) srcdoc 은 반드시 "프로퍼티 대입"으로 넣는다. innerHTML 로 iframe 태그를 조립하면
  //      문서 안의 따옴표로 속성을 탈출해 부모 페이지에 스크립트를 심을 수 있다.
  //   2) sandbox="" — allow-* 를 하나도 주지 않는다. allow-scripts 를 주는 순간 별도 오리진
  //      프록시가 필요해진다. 여기서 늘리지 말 것.
  //   3) 출처 칩은 iframe 밖(부모 DOM)에 만든다. 안에 두면 카드 템플릿이 출처를 위조할 수 있다.
  //   4) 속성은 전부 DOM 삽입(_append) 전에 세팅한다 — 삽입 시점에 sandbox 가 이미 걸려 있어야 한다.
  _onDisplay(e) {
    const d = this._parse(e)
    this._hideTyping()
    const adp = this.labelsValue.adp || {}

    const wrap = document.createElement("div")
    wrap.className = "ac-adp"

    // 출처 칩 — 앱명이 없으면 "%{app}" 자리가 빈 라벨이 되므로 검수 배지만 노출한다.
    // 표시명은 provider.label(사람이 읽는 앱명). provider.labelKey 는 기계용 식별자라 쓰지 않는다.
    const chip = document.createElement("div")
    chip.className = "ac-adp-chip"
    const app = (d.provider && d.provider.label) || ""
    if (app) {
      const src = document.createElement("span")
      src.className = "ac-adp-chip-src"
      src.textContent = (adp.provided_by || "").replace("%{app}", app)
      chip.appendChild(src)
    }
    const verified = document.createElement("span")
    verified.className = "ac-adp-chip-verified"
    verified.textContent = "✓ " + (adp.verified || "")
    chip.appendChild(verified)
    wrap.appendChild(chip)

    const frame = document.createElement("iframe")
    frame.className = "ac-adp-frame"
    frame.setAttribute("sandbox", "")
    frame.setAttribute("referrerpolicy", "no-referrer")
    frame.setAttribute("loading", "lazy")
    frame.title = adp.frame_title || ""
    // 높이는 런타임이 준 heightPx. 누락/비정상이면 320px 폴백(헬퍼 ADP_DEFAULT_HEIGHT_PX 와 동일 값).
    const height = parseInt(d.heightPx, 10)
    frame.height = String(height > 0 ? height : 320)
    frame.srcdoc = d.document || ""
    wrap.appendChild(frame)

    // 선언형 인텐트 칩 — iframe 밖(부모 DOM). 이력 파셜(_adp_card.html.erb)과 동일 DOM 계약.
    const actionRow = this._adpActions(d.actions)
    if (actionRow) wrap.appendChild(actionRow)

    this._append(wrap)
    // 카드는 툴 실행 산출물이라 뒤에 다음 스텝(LLM 재호출)이 이어진다 — 타이핑 재표시.
    this._showTyping()
  }

  // ── ADP 선언형 인텐트 칩 행 ── (ADP 스펙 §8.5) ──────────────
  // 런타임이 준 actions([{ label, command }])를 카드 iframe "밖" 플랫폼 네이티브 버튼으로 그린다.
  // 버튼을 앱이 아니라 플랫폼이 그리므로 "가짜 결제 버튼" 위조가 구조적으로 불가능하다.
  // 개수(≤8)·길이·단일 행 검증은 호스트가 이미 끝냈다 — 여기서는 형태 방어만 한다.
  //
  // ⚠ 보안: label 은 앱 문자열이므로 textContent 로만 넣는다(innerHTML 금지, 출처 칩과 동일 원칙).
  //   command 는 data-* 값으로만 다루고, 클릭 동작은 fillComposer 하나로 모은다.
  // @param {Array} actions 런타임 payload 의 actions (없으면 undefined)
  // @returns {HTMLElement|null} 칩이 하나도 없으면 null(빈 행을 만들지 않는다)
  _adpActions(actions) {
    if (!Array.isArray(actions) || actions.length === 0) return null

    const row = document.createElement("div")
    row.className = "ac-adp-actions"
    actions.forEach((a) => {
      // 라벨·명령 중 하나라도 비면 눌러도 아무 일이 없는 죽은 칩이라 건너뛴다(헬퍼 adp_actions 와 동일 기준).
      if (!a || !a.label || !a.command) return
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "ac-adp-action"
      btn.textContent = a.label
      // data-action 으로 자기 컨트롤러의 fillComposer 를 태운다 — 이력 칩과 완전히 같은 코드 경로.
      btn.dataset.action = "click->agent-chat#fillComposer"
      btn.dataset.agentChatCommandParam = a.command
      // 컴포저가 없는 화면(입력 비활성)에선 채울 곳이 없으므로 비활성.
      if (!this.hasInputTarget) btn.disabled = true
      row.appendChild(btn)
    })
    return row.children.length ? row : null
  }

  // ── 선언형 인텐트 칩 클릭 ── (ADP 스펙 §8.5) ────────────────
  // 컴포저에 command 를 채우고 포커스만 준다.
  //
  // ⚠ 자동 전송 절대 금지. 여기서 form.requestSubmit()·send()·전송 버튼 click() 을 부르면
  //   앱이 선언한 문자열이 사람 확인 없이 LLM 으로 흘러간다. command 는 반드시 사람이 평문으로
  //   읽고 편집·전송해야 하며, 그래야 기존 경로(스코프·RAR·WBA·네이티브 confirm)를 전량 재통과한다.
  // 라이브(_adpActions)와 이력(_adp_card.html.erb) 칩이 모두 이 메서드 하나를 탄다.
  // @param {Event} e Stimulus 액션 이벤트. e.params.command 에 채울 명령이 들어온다.
  fillComposer(e) {
    const command = (e.params && e.params.command) || ""
    if (!command || !this.hasInputTarget) return
    const input = this.inputTarget
    input.value = command
    input.focus()
    // 커서를 문장 끝으로 — 사용자가 곧바로 이어서 편집할 수 있게 한다.
    input.setSelectionRange(command.length, command.length)
  }

  _row(label, value) {
    const row = document.createElement("div")
    row.className = "ac-tool-row"
    const k = document.createElement("span")
    k.className = "ac-tool-key"
    k.textContent = label || ""
    const v = document.createElement("span")
    v.className = "ac-tool-val"
    // 툴 결과에 tx 해시가 실려 오는 경우가 가장 잦아 여기도 링크화한다.
    this._linkifyInto(v, value)
    row.appendChild(k)
    row.appendChild(v)
    return row
  }

  // ── 해시·주소 링크화 (DOM 조립) ────────────────────────────
  // WHAT: 텍스트 안의 `0x` + 40/64 hex 를 스캔 사이트 <a> 로 바꿔 el 에 넣는다.
  //
  // ⚠️ XSS 방어 계약 (절대 깨면 안 됨):
  //   - innerHTML 로 문자열을 재조립하지 않는다. 일반 구간은 createTextNode,
  //     링크 구간은 createElement("a") + textContent 로만 만든다.
  //   - href 는 scanUrlValue + 정규식에 걸린 `0x`+hex 만으로 조립한다. 모델이 뱉은
  //     임의 문자열이 URL 에 섞일 경로가 없다(javascript: 스킴 주입 불가).
  //   - 이력 렌더(ApplicationHelper#blockchain_linkify)와 판정 규칙이 같아야 한다.
  //
  // @param el   {Element} 내용을 채울 대상(기존 내용은 비운다)
  // @param text {*}       원본 텍스트(신뢰할 수 없는 입력)
  _linkifyInto(el, text) {
    const s = (text === null || text === undefined) ? "" : String(text)
    el.textContent = ""

    const base = this.scanUrlValue
    // hex 런 전체를 잡고 길이로 판별한다 — {64} 직접 매칭은 65자 이상에서 앞부분만
    // 잘라 "원본과 다른 해시"를 링크로 만든다(Ruby 헬퍼 주석과 동일 사유).
    const re = /0x[0-9a-fA-F]+/g
    let last = 0
    let m

    while ((m = re.exec(s)) !== null) {
      const hexLen = m[0].length - 2
      // 단어 중간의 0x(예: cafe0x12..) 제외. lookbehind 는 구형 사파리 미지원이라
      // 직전 문자를 인덱스로 검사한다 — Ruby 쪽 lookbehind 와 판정 결과가 같다.
      const prev = m.index > 0 ? s[m.index - 1] : ""
      const linkable = base && !/[0-9A-Za-z]/.test(prev) &&
                       (hexLen === 40 || hexLen === 64)
      if (!linkable) continue

      if (m.index > last) el.appendChild(document.createTextNode(s.slice(last, m.index)))

      const a = document.createElement("a")
      a.href = base + (hexLen === 64 ? "/tx/" : "/address/") + m[0]
      a.target = "_blank"
      a.rel = "noopener noreferrer"
      a.title = m[0]
      a.className = "chain-link"
      a.textContent = this._shortenHash(m[0])
      el.appendChild(a)

      last = m.index + m[0].length
    }

    // 남은 꼬리(매치가 하나도 없으면 전체)를 텍스트 노드로 붙인다.
    if (last < s.length) el.appendChild(document.createTextNode(s.slice(last)))
  }

  // 해시·주소 축약(앞6…뒤4) — Ruby blockchain_shorten 과 동일 규칙.
  _shortenHash(value) {
    return value.length <= 12 ? value : value.slice(0, 6) + "…" + value.slice(-4)
  }

  // ── 상태 갱신 ──────────────────────────────────────────────
  _onStatus(e) {
    const d = this._parse(e)
    const label = (this.labelsValue.statuses && this.labelsValue.statuses[d.status]) || d.status || ""
    this._setStatus(label)
    // running = 생성 시작 → 타이핑 표시. waiting_confirm = 사람 승인 대기 → 타이핑 제거.
    if (d.status === "running") this._showTyping()
    if (d.status === "waiting_confirm") this._hideTyping()
    // 종료 상태(completed/stopped/error)면 타이핑·펄스를 감추고 스트림을 닫는다("done" 아님 —
    // 여기서 EventSource 를 닫아야 서버 res.end 후 자동 재연결→버퍼 리플레이(빈 박스 무한)를 막는다).
    if (d.status === "completed" || d.status === "done" || d.status === "stopped" || d.status === "error") {
      this._hideTyping()
      if (this.hasPulseTarget) this.pulseTarget.style.display = "none"
      if (this._source) this._source.close()
    }
  }

  // ── 고위험 승인 프롬프트 ───────────────────────────────────
  _onConfirm(e) {
    const d = this._parse(e)
    this._hideTyping()
    const box = document.createElement("div")
    box.className = "ac-confirm"

    const title = document.createElement("div")
    title.className = "ac-confirm-title"
    title.textContent = (this.labelsValue.confirm_title || "") + " " + (d.tool || "")
    box.appendChild(title)

    box.appendChild(this._row(this.labelsValue.args, this._stringify(d.args)))

    // 승인/거부 폼 — 인플레이스 fetch 로 confirm 엔드포인트에 POST 한다(페이지 이동 없음).
    box.appendChild(this._confirmForm(d.toolCallId, "true", this.labelsValue.approve))
    box.appendChild(this._confirmForm(d.toolCallId, "false", this.labelsValue.reject))
    this._append(box)
  }

  // 승인/거부 폼 한 벌(버튼 1개 + hidden 필드)을 만든다.
  // submit 은 _submitConfirm 이 가로채 fetch 로 보내므로 실제 폼 전송(페이지 이동)은 일어나지 않는다.
  // method/action 은 남겨둔다 — 필드 구성이 서버 계약과 1:1 로 보이고, FormData 조립의 단일 출처가 된다.
  // @param toolCallId [String] 승인 대상 툴콜 id
  // @param approve    [String] "true"=승인 / "false"=거부 (서버가 문자열 비교)
  // @param label      [String] 버튼 라벨(i18n · labelsValue 주입값)
  _confirmForm(toolCallId, approve, label) {
    const form = document.createElement("form")
    form.method = "post"
    form.action = this.confirmUrlValue
    form.className = "ac-confirm-form"

    const token = document.querySelector('meta[name="csrf-token"]')
    form.appendChild(this._hidden("authenticity_token", token ? token.content : ""))
    form.appendChild(this._hidden("conversation_id", this.conversationIdValue))
    form.appendChild(this._hidden("tool_call_id", toolCallId || ""))
    form.appendChild(this._hidden("approve", approve))
    // /talk 컨텍스트에서만 return_view 를 함께 실어 (HTML 폴백 경로에서) 승인/거부 후에도 /talk 에 머물게 한다.
    if (this.hasReturnViewValue && this.returnViewValue) {
      form.appendChild(this._hidden("return_view", this.returnViewValue))
    }

    const btn = document.createElement("button")
    btn.type = "submit"
    btn.className = "ac-confirm-btn ac-confirm-btn--" + (approve === "true" ? "yes" : "no")
    btn.textContent = label || ""
    form.appendChild(btn)
    // 폼 전송을 가로채 인플레이스 POST 로 전환한다(리다이렉트→전체 리로드→스크롤 튐 방지).
    form.addEventListener("submit", (e) => this._submitConfirm(e))
    return form
  }

  // ── 승인/거부 인플레이스 전송 ──────────────────────────────
  // WHAT: confirm 폼 submit 을 가로채 send() 와 같은 방식으로 fetch POST 한다.
  // WHY:  기존 HTML 폼 전송은 서버 redirect → 전체 페이지 리로드를 유발하고, 리로드 후
  //       connect() 의 _scrollToBottom() 때문에 보던 위치가 튄다. 승인은 대화 도중의 조작이라
  //       화면이 그 자리에서 이어져야 한다.
  // CAUTION: 여기서 EventSource 를 다시 열지 않는다. waiting_confirm 상태에서는 스트림을 닫지
  //          않으므로(_onStatus 참조) 기존 구독이 그대로 살아 있고, 승인 직후 run 이 재개되며
  //          후속 이벤트가 이어서 흐른다. 재구독하면 버퍼 리플레이로 이벤트가 중복된다.
  // @param e [SubmitEvent] 승인/거부 폼의 submit 이벤트
  async _submitConfirm(e) {
    e.preventDefault()
    const form = e.currentTarget
    const box = form.closest(".ac-confirm")
    // 중복 클릭 방지 — 응답 전까지 그 박스의 승인/거부 버튼을 모두 잠근다.
    const buttons = box ? Array.from(box.querySelectorAll(".ac-confirm-btn")) : []
    buttons.forEach((b) => { b.disabled = true })

    const token = document.querySelector('meta[name="csrf-token"]')
    // 폼 hidden 필드(conversation_id / tool_call_id / approve / return_view)를 그대로 body 로 옮긴다.
    const body = new URLSearchParams(new FormData(form))

    let res
    try {
      res = await fetch(form.action, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/x-www-form-urlencoded",
          "X-CSRF-Token": token ? token.content : ""
        },
        body: body.toString()
      })
    } catch (_) {
      // 네트워크 실패 — 버튼을 되살려 사용자가 다시 시도할 수 있게 한다.
      buttons.forEach((b) => { b.disabled = false })
      this._setStatus(this.labelsValue.command_failed || "")
      return
    }
    if (!res.ok) {
      buttons.forEach((b) => { b.disabled = false })
      this._setStatus(this.labelsValue.command_failed || "")
      return
    }

    // 성공 — 고른 쪽 버튼만 남기고(선택 결과를 눈에 보이게) 박스를 완료 상태로 잠근다.
    if (box) {
      box.classList.add("ac-confirm--done")
      box.querySelectorAll(".ac-confirm-form").forEach((f) => { if (f !== form) f.remove() })
    }
    // run 재개 — 다음 이벤트가 올 때까지 진행감을 준다.
    this._showTyping()
  }

  _hidden(name, value) {
    const i = document.createElement("input")
    i.type = "hidden"
    i.name = name
    i.value = value
    return i
  }

  _stringify(v) {
    if (v === null || v === undefined) return ""
    if (typeof v === "string") return v
    try { return JSON.stringify(v) } catch (_) { return String(v) }
  }

  _append(node) {
    if (!this.hasStreamTarget) return
    this.streamTarget.appendChild(node)
    // 라이브 컨테이너는 display:contents 라 스크롤 박스가 아니다 → 실제 스크롤러(scroller)를 내린다.
    this._scrollToBottom()
  }

  // 스트림 스크롤러를 하단으로(새 메시지·이력 로드 시 최신 노출).
  _scrollToBottom() {
    if (this.hasScrollerTarget) this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight
  }

  _setStatus(text) {
    if (this.hasStatusTarget) this.statusTarget.textContent = text
  }
}
